import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";

/**
 * ImageInput — reusable drag & drop image picker with live preview.
 *
 * Works for both create (no existing image) and edit (shows the
 * current remote image until a new one is chosen). Fully controlled
 * from the parent via `onChange(file)`.
 *
 * Props:
 *  - label       : field label
 *  - onChange    : (File|null) => void
 *  - existingUrl : current remote image URL (edit mode)
 *  - required    : show a required asterisk
 *  - error       : validation error string
 *  - accept      : accept attr (default images)
 */
const ImageInput = ({
  label,
  onChange,
  existingUrl = null,
  required = false,
  error = "",
  accept = "image/*",
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(existingUrl);
  const [dragOver, setDragOver] = useState(false);
  const objectUrlRef = useRef(null);

  // Keep preview in sync if the existing URL changes (async edit load).
  useEffect(() => {
    if (existingUrl && !objectUrlRef.current) setPreview(existingUrl);
  }, [existingUrl]);

  // Revoke any object URL on unmount to avoid memory leaks.
  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    []
  );

  const applyFile = useCallback(
    (file) => {
      if (!file) return;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setPreview(url);
      onChange && onChange(file);
    },
    [onChange]
  );

  const onSelect = (e) => applyFile(e.target.files && e.target.files[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) applyFile(file);
  };

  const clear = (e) => {
    e.stopPropagation();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
    onChange && onChange(null);
  };

  return (
    <Form.Group className="my-2">
      {label && (
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      <div
        onClick={() => !disabled && inputRef.current && inputRef.current.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={disabled ? (e) => e.preventDefault() : onDrop}
        style={{
          border: `2px dashed ${error ? "#dc3545" : dragOver ? "#0093ed" : "#cfd4da"}`,
          borderRadius: 8,
          padding: preview ? 10 : 24,
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          background: disabled ? "#f0f0f0" : dragOver ? "#f0f8ff" : "#fafbfc",
          opacity: disabled ? 0.65 : 1,
          transition: "all .15s ease",
          position: "relative",
        }}
      >
        {preview ? (
          <div className="position-relative d-inline-block">
            <img
              src={preview}
              alt="preview"
              style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 6 }}
            />
            <button
              type="button"
              onClick={clear}
              disabled={disabled}
              title="Remove"
              className="btn btn-danger d-flex align-items-center justify-content-center"
              style={{
                position: "absolute",
                top: -8,
                right: -8,
                width: 22,
                height: 22,
                padding: 0,
                borderRadius: "50%",
                lineHeight: 1,
                fontSize: 14,
              }}
            >
              &times;
            </button>
          </div>
        ) : (
          <div className="text-muted">
            <i className="bx bxs-cloud-upload" style={{ fontSize: 28 }}></i>
            <div>Drag &amp; drop or click to upload</div>
            <small>PNG, JPG, WEBP, SVG — up to 10MB</small>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onSelect}
          disabled={disabled}
          hidden
        />
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </Form.Group>
  );
};

export default ImageInput;
